// ===========================================
// Admin Teacher View Context - Provider Component
// ===========================================
// Allows users with multiple roles/access levels to switch between views.
// Available views are computed from the authenticated user's identity:
//
//   ADMIN                         → ['admin'] + 'teacher' if isAlsoTeacher
//   TEACHER with staff role       → ['staff', 'teacher']
//   PARENT with staff role        → ['staff', 'parent']
//   PARENT only                   → ['parent']
//
// No 'parent' view for a non-PARENT login, even one with a parent record (`isAlsoParent`): the
// parent shell is PARENT-only (`ProtectedRoute requiredRole="PARENT"`) and so is the parent API
// (`parentOnly`), so the entry could never open (roles slice 2 step 9 /qa L5).

import { useState, useCallback, ReactNode, useEffect, useMemo } from 'react';
import { AdminTeacherViewContext, AdminTeacherView } from './AdminTeacherViewContext.types';
import { useAuth } from './useAuth';

const STORAGE_KEY = 'adminTeacherViewMode';

// Provider component
export function AdminTeacherViewProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  // Derive the ordered list of views available to this user
  const availableViews = useMemo<AdminTeacherView[]>(() => {
    if (!user) return ['admin'];

    const views: AdminTeacherView[] = [];

    if (user.role === 'ADMIN') {
      views.push('admin');
      if (user.isAlsoTeacher) views.push('teacher');
      return views;
    }

    if (user.role === 'TEACHER') {
      if (user.isStaff) views.push('staff');
      views.push('teacher');
      return views;
    }

    if (user.role === 'PARENT') {
      if (user.isStaff) views.push('staff');
      views.push('parent');
      return views;
    }

    // STUDENT or any other role — no multi-view switching needed,
    // return 'admin' as a neutral fallback so it falls through to role-based nav
    return ['admin'];
  }, [user]);

  const [currentView, setCurrentView] = useState<AdminTeacherView>(() => {
    // Restore from localStorage, validating against the initial user's views below
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (
        saved === 'admin' ||
        saved === 'staff' ||
        saved === 'teacher' ||
        saved === 'parent'
      ) {
        return saved as AdminTeacherView;
      }
    } catch {
      // Ignore storage errors
    }
    return 'admin';
  });

  // Whenever availableViews changes (e.g. after the user object loads),
  // validate that currentView is still in the list. If not, reset to the first available.
  useEffect(() => {
    if (!availableViews.includes(currentView)) {
      setCurrentView(availableViews[0]);
    }
  }, [availableViews, currentView]);

  // Persist to localStorage whenever the active view changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, currentView);
  }, [currentView]);

  // Set a specific view (ignored if not in availableViews)
  const setView = useCallback(
    (view: AdminTeacherView) => {
      if (availableViews.includes(view)) {
        setCurrentView(view);
      }
    },
    [availableViews],
  );

  // Cycle forward through availableViews
  const toggleView = useCallback(() => {
    setCurrentView((prev) => {
      const idx = availableViews.indexOf(prev);
      const nextIdx = (idx + 1) % availableViews.length;
      return availableViews[nextIdx];
    });
  }, [availableViews]);

  return (
    <AdminTeacherViewContext.Provider
      value={{
        currentView,
        setView,
        toggleView,
        availableViews,
      }}
    >
      {children}
    </AdminTeacherViewContext.Provider>
  );
}
